import { useCallback, useEffect, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { logError, logWarn } from 'lib/logging'

export type TestContact = {
  id?: string
  name?: string
  cellPhone?: string
  homePhone?: string
  workPhone?: string
  appointmentStatus?: string
  tags?: string[]
  createdDate?: string
}

export type FakeContactsActionData =
  | { error: string }
  | { success: true; contactId?: string }
  | { success: true; deleted: string }

export type UseFakeContactsReturn = {
  testPhoneNumbers: string[]
  locations: { id: string; name: string }[]
  testContacts: TestContact[]
  hasWorkspace: boolean
  actionData: FakeContactsActionData | undefined
  isCreating: boolean
  isDeleting: boolean
  onCreate: (fields: Record<string, string>) => void
  onDelete: (contactId: string) => void
}

/**
 * Runs a create or delete on `/api/fake-contacts`. The API reads a JSON body, so the action goes
 * as JSON, not the FormData this page used to send (which the API rejected with a 500 parse error,
 * TRK-5926). A failed request, including a 500 whose body is not JSON, comes back as an `error`
 * the page shows, instead of a rejection only the console sees.
 */
export async function postFakeContactsAction(body: Record<string, string>): Promise<FakeContactsActionData> {
  try {
    const res = await fetch(`/api/fake-contacts`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify(body),
    })
    const text = await res.text()
    let parsed: (FakeContactsActionData & { error?: string }) | undefined
    try {
      parsed = JSON.parse(text)
    } catch {
      logWarn(`Fake contacts response was not JSON`, { status: res.status, body: text.slice(0, 200) })
      parsed = undefined
    }
    if (!res.ok || !parsed) return { error: parsed?.error ?? `Request failed (${res.status})` }
    return parsed
  } catch (err) {
    logError(`Error posting fake contacts action`, err)
    return { error: `Request failed. Check your connection and try again.` }
  }
}

export function useFakeContacts(): UseFakeContactsReturn {
  const [confirm] = useConfirmation()
  const [data, setData] = useState<{
    testPhoneNumbers: string[]
    locations: { id: string; name: string }[]
    testContacts: TestContact[]
    hasWorkspace: boolean
  }>({ testPhoneNumbers: [], locations: [], testContacts: [], hasWorkspace: false })
  const [actionData, setActionData] = useState<FakeContactsActionData>()
  const [isCreating, setIsCreating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const fetchData = useCallback(() => {
    fetch(`/api/fake-contacts`, { credentials: `include` })
      .then((res) => res.json())
      .then((d) => setData(d))
      .catch((err) => logError(`Error fetching fake contacts`, err))
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onCreate = (fields: Record<string, string>) => {
    setIsCreating(true)
    postFakeContactsAction({ ...fields, _action: `create` })
      .then((result) => {
        setActionData(result)
        fetchData()
      })
      .finally(() => setIsCreating(false))
  }

  const onDelete = (contactId: string) => {
    confirm({
      title: `Delete Contact`,
      description: `This will delete the contact and all associated data (messages, activities, appointments). Continue?`,
      variant: `danger`,
    }).then(() => {
      setIsDeleting(true)
      postFakeContactsAction({ _action: `delete`, contactId })
        .then((result) => {
          setActionData(result)
          fetchData()
        })
        .finally(() => setIsDeleting(false))
    })
  }

  return {
    testPhoneNumbers: data.testPhoneNumbers,
    locations: data.locations,
    testContacts: data.testContacts,
    hasWorkspace: data.hasWorkspace,
    actionData,
    isCreating,
    isDeleting,
    onCreate,
    onDelete,
  }
}
